import { useAppContext } from '@/context';
import { logout } from '@/utils/auth';
import { getJson } from '@/utils/fetchOptions';

import { useAuthToken, useQuery } from '..';

export type ContentHubCollaborationNode = {
  id: string;
  influencerName: string;
  influencerHandle: string;
};

export type ContentHubCampaignNode = {
  id: string;
  name: string;
  collaborations: ContentHubCollaborationNode[];
};

export type ContentHubBrandNode = {
  id: string;
  name: string;
  logoUrl: string;
  campaigns: ContentHubCampaignNode[];
};

export type ContentHubTeamNode = {
  id: string;
  name: string;
  brands: ContentHubBrandNode[];
};

function buildTree(rows: Api.Client.ContentHubPathRow[]): ContentHubTeamNode[] {
  const teams = new Map<string, ContentHubTeamNode>();

  rows.forEach((row) => {
    const teamId = row.team_id;
    const brandId = row.brand_id;
    const campaignId = row.campaign_id;
    const collaborationId = row.collaboration_id;

    let team = teams.get(teamId);
    if (!team) {
      team = { id: teamId, name: row.team_name, brands: [] };
      teams.set(teamId, team);
    }

    let brand = team.brands.find((b) => b.id === brandId);
    if (!brand) {
      brand = {
        id: brandId,
        name: row.brand_name,
        logoUrl: row.brand_logo_url,
        campaigns: []
      };
      team.brands.push(brand);
    }

    let campaign = brand.campaigns.find((c) => c.id === campaignId);
    if (!campaign) {
      campaign = { id: campaignId, name: row.campaign_name, collaborations: [] };
      brand.campaigns.push(campaign);
    }

    if (!campaign.collaborations.some((c) => c.id === collaborationId)) {
      campaign.collaborations.push({
        id: collaborationId,
        influencerName: row.influencer_name,
        influencerHandle: row.influencer_handle
      });
    }
  });

  // Stable sort for nicer UX
  const sortedTeams = Array.from(teams.values()).sort((a, b) => a.name.localeCompare(b.name));
  sortedTeams.forEach((team) => {
    team.brands.sort((a, b) => a.name.localeCompare(b.name));
    team.brands.forEach((brand) => {
      brand.campaigns.sort((a, b) => a.name.localeCompare(b.name));
      brand.campaigns.forEach((campaign) => {
        campaign.collaborations.sort((a, b) =>
          `${a.influencerName}${a.influencerHandle}`.localeCompare(
            `${b.influencerName}${b.influencerHandle}`
          )
        );
      });
    });
  });

  return sortedTeams;
}

export function useContentHubPaths() {
  const { setAlert } = useAppContext();
  const token = useAuthToken();

  const {
    data = {},
    error,
    isLoading
  } = useQuery({
    // v2: tree shape changed from Team->Campaign to Team->Brand->Campaign
    queryKey: 'client-contenthub-paths-v2',
    queryData: token,
    queryFn: async () => {
      return getJson({
        url: `${process.env.NEXT_PUBLIC_BEATLY_API_URL}/app/v2/contenthub/paths`,
        token
      }).then(
        (response: Api.Client.ContentHubPathsResponse & ResponseObject & ResponseExpiredToken) => {
          if ((response as ResponseExpiredToken).isExpired) {
            logout();
          }

          if ('code' in response) {
            setAlert(response.code);
          }

          const rows = response.folders ?? [];
          return {
            rows,
            tree: buildTree(rows)
          };
        }
      );
    },
    enabled: !!token
  });

  return {
    isLoading,
    error,
    data: data as { rows: Api.Client.ContentHubPathRow[]; tree: ContentHubTeamNode[] }
  };
}
